import React, { forwardRef } from "react";
import DatePicker from "react-datepicker";
import { Input } from "@/lib/shadcn/ui/input";
import { cn } from "@/lib/shadcn/utils";
import { CalendarIcon } from "lucide-react";
import "react-datepicker/dist/react-datepicker.css";

interface DateInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "type" | "value" | "onChange"> {
    value?: Date | null | undefined;
    onChange?: (date: Date | null) => void;
}

/**
 * Custom date input component with DD/MM/YYYY format using react-datepicker
 * Enforces European date format regardless of browser locale
 */
const DateInput = forwardRef<HTMLInputElement, DateInputProps>(
    ({ className, value, onChange, placeholder = "dd/mm/yyyy", disabled, ...props }, ref) => {
        // Custom input component with built-in calendar icon
        const CustomInput = React.forwardRef<HTMLInputElement, any>((inputProps, inputRef) => (
            <div className="relative">
                <Input {...inputProps} className={cn("pr-10", className)} ref={inputRef} />
                <CalendarIcon className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
            </div>
        ));

        CustomInput.displayName = "CustomInput";

        return (
            <DatePicker
                selected={value}
                onChange={onChange}
                dateFormat="dd/MM/yyyy"
                placeholderText={placeholder}
                showPopperArrow={false}
                disabled={disabled}
                customInput={<CustomInput ref={ref} disabled={disabled} {...props} />}
                popperClassName="z-50"
                calendarClassName="shadow-lg border border-gray-200 rounded-md bg-white"
            />
        );
    },
);

DateInput.displayName = "DateInput";

export { DateInput };
